import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/store/auth';
import { toast } from '@/store/ui';
import { useSocket, useSocketEvent } from '@/realtime/socketContext';
import { useEasyRtc } from './useEasyRtc';
import { RtcSessionContext, type RtcSessionDescriptor } from './rtcSessionContext';

type SafariPiPVideo = HTMLVideoElement & {
  autoPictureInPicture?: boolean;
  webkitPresentationMode?: 'inline' | 'picture-in-picture' | 'fullscreen';
  webkitSupportsPresentationMode?: (mode: string) => boolean;
  webkitSetPresentationMode?: (mode: string) => void;
};

/**
 * Owns the live media connection above the page router. Changing from a
 * meeting to Projects, Tasks or Chat therefore changes only the visible page;
 * it no longer unmounts EasyRTC and stops the camera.
 */
export function RtcSessionProvider({ children }: { children: ReactNode }) {
  const { user, orgId } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const [session, setSession] = useState<RtcSessionDescriptor | null>(null);
  const pipVideoRef = useRef<SafariPiPVideo>(null);
  const sessionId = session?.id;
  const sessionKind = session?.kind;

  const rtc = useEasyRtc({
    meetingId: session?.id ?? null,
    displayName: user?.name ?? 'Guest',
    orgId,
    kind: session?.kind ?? 'meeting',
    startWithVideo: session?.startWithVideo ?? true,
    startWithAudio: session?.startWithAudio ?? true,
    devices: session?.devices,
  });

  const primary = useMemo(() => {
    const screenPeer = rtc.peers.find((peer) => peer.streams.screen);
    if (screenPeer?.streams.screen) {
      return {
        stream: screenPeer.streams.screen,
        label: `${screenPeer.name} is presenting`,
        local: false,
      };
    }
    if (rtc.screenStream) {
      return { stream: rtc.screenStream, label: 'You are presenting', local: true };
    }
    const cameraPeer = rtc.peers.find((peer) => peer.streams.camera);
    if (cameraPeer?.streams.camera) {
      return { stream: cameraPeer.streams.camera, label: cameraPeer.name, local: false };
    }
    return {
      stream: rtc.camOn ? rtc.localStream : null,
      label: user?.name ?? 'You',
      local: true,
    };
  }, [rtc.peers, rtc.screenStream, rtc.localStream, rtc.camOn, user?.name]);

  const pipSupported = useMemo(() => {
    if (typeof document === 'undefined') return false;
    const probe = document.createElement('video') as SafariPiPVideo;
    return Boolean(
      document.pictureInPictureEnabled ||
      probe.webkitSupportsPresentationMode?.('picture-in-picture'),
    );
  }, []);

  const startSession = useCallback((next: RtcSessionDescriptor) => {
    setSession((current) => {
      if (
        current?.id === next.id &&
        current.kind === next.kind &&
        current.title === next.title &&
        current.startWithVideo === next.startWithVideo
        && current.startWithAudio === next.startWithAudio
      ) {
        return current;
      }
      return next;
    });
  }, []);

  const endSession = useCallback(() => {
    setSession(null);
  }, []);

  // Presence must live as long as the persistent media session, not as long
  // as the full-screen meeting route happens to be visible.
  useEffect(() => {
    if (!socket || !sessionId || !sessionKind) return;
    if (sessionKind === 'meeting') {
      socket.emit('meeting:join', { meetingId: sessionId, via: 'call' });
    }
    return () => {
      if (sessionKind === 'meeting') {
        socket.emit('meeting:leave', { meetingId: sessionId, via: 'call' });
      } else socket.emit('call:leave', { callId: sessionId });
    };
  }, [socket, sessionId, sessionKind]);

  useSocketEvent('meeting:control', (control) => {
    if (!session || session.kind !== 'meeting' || control.meetingId !== session.id) return;
    if (control.action === 'mute_all' && rtc.micOn) {
      rtc.toggleMic();
      toast.info('You were muted by the host');
    }
    if (control.action === 'mute' && control.targetUserId === user?.id && rtc.micOn) {
      rtc.toggleMic();
      toast.info('You were muted by the host');
    }
    if (control.action === 'remove' && control.targetUserId === user?.id) {
      toast.error('You were removed from the meeting');
      endSession();
      navigate('/meetings');
    }
    if (control.action === 'end') {
      toast.info('The host ended this meeting');
      endSession();
      navigate('/meetings');
    }
  });

  useSocketEvent('call:ended', ({ callId }) => {
    if (!session || session.kind !== 'call' || callId !== session.id) return;
    endSession();
    const nativeWindow = window as Window & {
      ReactNativeWebView?: { postMessage: (message: string) => void };
    };
    nativeWindow.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'call:ended' }));
    navigate('/chat');
  });

  // A dedicated persistent video is the stable source handed to the browser's
  // system PiP window. It stays mounted while the visible route changes.
  useEffect(() => {
    const video = pipVideoRef.current;
    if (!video) return;
    video.autoPictureInPicture = true;
    video.disablePictureInPicture = false;
    if (video.srcObject !== primary.stream) video.srcObject = primary.stream;
    if (primary.stream) void video.play().catch(() => undefined);
  }, [primary.stream]);

  const enterPictureInPicture = useCallback(async () => {
    const video = pipVideoRef.current;
    const hasLiveVideo = Boolean(
      primary.stream
        ?.getVideoTracks()
        .some((track) => track.readyState === 'live' && track.enabled),
    );
    if (!video || !hasLiveVideo) {
      toast.info(
        'PiP needs an active camera or screen share',
        'Audio will keep playing in the background.',
      );
      return;
    }

    try {
      if (document.pictureInPictureEnabled && document.pictureInPictureElement !== video) {
        await video.requestPictureInPicture();
        return;
      }
      if (video.webkitSupportsPresentationMode?.('picture-in-picture')) {
        video.webkitSetPresentationMode?.('picture-in-picture');
        return;
      }
      toast.info('Picture-in-Picture is not supported by this browser');
    } catch (error) {
      // Automatic PiP can be denied until the user taps the PiP control once.
      if (document.visibilityState === 'visible') {
        toast.error(
          'Could not open Picture-in-Picture',
          error instanceof Error ? error.message : 'Try the PiP button again.',
        );
      }
    }
  }, [primary.stream]);

  /**
   * Closes the floating window again.
   *
   * Deliberately narrow: it only acts on the video this provider owns, so a
   * PiP the user opened on some other video is left alone.
   */
  const exitPictureInPicture = useCallback(async () => {
    const video = pipVideoRef.current;
    if (!video) return;
    try {
      if (document.pictureInPictureElement === video) {
        await document.exitPictureInPicture();
        return;
      }
      if (video.webkitPresentationMode === 'picture-in-picture') {
        video.webkitSetPresentationMode?.('inline');
      }
    } catch {
      // Nothing to do: the window is already closed, or the browser refused.
    }
  }, []);

  // Best-effort automatic handoff when the user changes apps or backgrounds
  // the browser. Browsers that require a fresh gesture still have the visible
  // PiP control in the meeting and mini-player.
  useEffect(() => {
    if (!session) return;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        void enterPictureInPicture();
        return;
      }
      // Coming back to the tab hands the call to the page again, so the
      // floating window is now a second copy of what is already on screen.
      // Without this it outlived the handoff and sat on top of the meeting.
      void exitPictureInPicture();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [session, enterPictureInPicture, exitPictureInPicture]);

  // Leaving the call should never leave its picture behind.
  useEffect(() => {
    if (!session) void exitPictureInPicture();
  }, [session, exitPictureInPicture]);

  const value = useMemo(
    () => ({
      session,
      rtc,
      primaryStream: primary.stream,
      primaryLabel: primary.label,
      primaryIsLocal: primary.local,
      pipSupported,
      startSession,
      endSession,
      enterPictureInPicture,
    }),
    [session, rtc, primary, pipSupported, startSession, endSession, enterPictureInPicture],
  );

  return (
    <RtcSessionContext.Provider value={value}>
      {children}
      <video
        ref={pipVideoRef}
        playsInline
        autoPlay
        muted
        aria-hidden="true"
        className="pointer-events-none fixed h-px w-px opacity-0"
      />
    </RtcSessionContext.Provider>
  );
}
